/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * Whether travel UI should show on a shared page (lead, deal, account, case,
 * invoice, search).
 *
 * Two conditions, both required: the tenant holds the TRAVEL edition, and the
 * company-level `is_travel_enabled` toggle is on. The toggle stays because a
 * travel tenant can switch the travel surfaces off from Settings; the edition
 * is what stops a tenant who never bought Travel (a Real Estate tenant cloned
 * from a template with the flag set) from seeing it.
 *
 * Replaces the per-page /company/info fetch + travelFeatureUpdated listener
 * that each of those pages carried.
 */

import { useEffect, useState } from "react";
import { useAuth } from "../auth/Auth";
import { fetchCompanyInfo, invalidateCompanyInfo } from "../utils/companyInfo";
import { useModuleEntitlements } from "./useModuleEntitlements";

const readFlag = (data) =>
  data?.is_travel_enabled === true || data?.is_travel_enabled === "true";

export function useTravelUi() {
  const { isAuthReady, isAuthenticated, token } = useAuth();
  const { hasEdition, loading: entitlementsLoading } = useModuleEntitlements();
  const [isTravelFlagOn, setIsTravelFlagOn] = useState(false);
  const [flagLoaded, setFlagLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!isAuthReady || !isAuthenticated) return;
      try {
        const data = await fetchCompanyInfo();
        if (!cancelled) setIsTravelFlagOn(readFlag(data));
      } catch {
        if (!cancelled) setIsTravelFlagOn(false);
      } finally {
        if (!cancelled) setFlagLoaded(true);
      }
    };
    load();

    const handleTravelUpdate = (event) => {
      invalidateCompanyInfo();
      setIsTravelFlagOn(event?.detail?.enabled === true);
      setFlagLoaded(true);
    };
    window.addEventListener("travelFeatureUpdated", handleTravelUpdate);
    return () => {
      cancelled = true;
      window.removeEventListener("travelFeatureUpdated", handleTravelUpdate);
    };
  }, [isAuthReady, isAuthenticated, token]);

  const hasTravelEdition = hasEdition("TRAVEL");
  return {
    showTravel: hasTravelEdition && isTravelFlagOn,
    hasTravelEdition,
    isTravelFlagOn,
    loading: !flagLoaded || entitlementsLoading,
  };
}

export default useTravelUi;
